import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Link,
  Box,
  Button,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Stack,
  Typography,
} from "@mui/material";
import {
  RO_ACCOUNTING_OPTIONS,
  getAccountingLabel,
} from "./accountingPlanRo";
import { getInvoiceIssueDate, sortInvoicesByIssueDate } from "./invoiceIssueDate";


function parseAccountingTags(value) {
  return String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

const defaultForm = {
  serviciu: "",
  denumire: "",
  cont_contabil: "",
  valuta: "RON",
  suma_valuta: "",
  zi_emitere: 1,
  zile_scadenta: 0,
  active: 1,
};

function RecurentTab({
  recurrences,
  invoices,
  onCreateRecurrence,
  onGenerateNow,
  onRunJob,
  onPredictCurrentMonth,
  onOpenVendor,
}) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(defaultForm);
  const [issueDateOrder, setIssueDateOrder] = useState("desc");

  const generatedInvoices = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    const parseDate = (v) => {
      const d = String(v || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
      const dt = new Date(`${d}T00:00:00`);
      return Number.isNaN(dt.getTime()) ? null : dt;
    };

    const toMonthLabel = (row) => {
      const rawMonth = String(row?.luna || "").trim();
      if (/^\d{4}-\d{2}$/.test(rawMonth)) return rawMonth;
      const emitere = String(row?.data_emitere || "").slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(emitere)) return emitere.slice(0, 7);
      return "";
    };

    const rows = (invoices || []).filter((row) => {
      if (String(row?.tip_plata || "").toUpperCase() !== "RECURRENT") return false;

      const rowStatus = String(row?.status || "").toUpperCase();
      const monthLabel = toMonthLabel(row);
      const dueDate = parseDate(row?.termen_scadent);
      const isOpenLike = ["OPEN", "PARTIAL", "DRAFT"].includes(rowStatus);
      const isCurrentMonth = monthLabel === currentMonth;
      const isOldOverdue = Boolean(
        monthLabel &&
          monthLabel < currentMonth &&
          isOpenLike &&
          dueDate &&
          dueDate < today
      );

      return isCurrentMonth || isOldOverdue;
    });

    return sortInvoicesByIssueDate(rows, issueDateOrder);
  }, [invoices, issueDateOrder]);

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} className="sf-inner-tabs">
        <Tab label="Recurrence templates" />
        <Tab label="Generated invoices" />
      </Tabs>

      {tab === 0 && (
        <Box className="sf-recur-grid">
          <Paper className="sf-form-card">
            <TextField
              label="Serviciu"
              value={form.serviciu}
              onChange={(e) => setForm((s) => ({ ...s, serviciu: e.target.value }))}
            />
            <TextField
              label="Denumire"
              value={form.denumire}
              onChange={(e) => setForm((s) => ({ ...s, denumire: e.target.value }))}
            />
            <Autocomplete
              multiple
              freeSolo
              options={RO_ACCOUNTING_OPTIONS}
              getOptionLabel={(option) => {
                if (typeof option === "string") return getAccountingLabel(option.split(" - ")[0] || option);
                return option?.label || "";
              }}
              value={parseAccountingTags(form.cont_contabil)}
              onChange={(_, value) =>
                setForm((s) => ({
                  ...s,
                  cont_contabil: (value || [])
                    .map((v) => {
                      if (typeof v === "string") return String(v).split(" - ")[0].trim();
                      return String(v?.code || "").trim();
                    })
                    .filter(Boolean)
                    .join(", "),
                }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Cont contabil (taguri)" placeholder="Adauga cont..." />
              )}
            />
            <TextField
              select
              label="Valuta"
              value={form.valuta}
              onChange={(e) => setForm((s) => ({ ...s, valuta: e.target.value }))}
            >
              <MenuItem value="RON">RON</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </TextField>
            <TextField
              label="Suma"
              type="number"
              value={form.suma_valuta}
              onChange={(e) => setForm((s) => ({ ...s, suma_valuta: e.target.value }))}
            />
            <TextField
              label="Zi emitere"
              type="number"
              value={form.zi_emitere}
              onChange={(e) => setForm((s) => ({ ...s, zi_emitere: Number(e.target.value) }))}
            />
            <TextField
              label="Zile scadenta"
              type="number"
              value={form.zile_scadenta}
              onChange={(e) => setForm((s) => ({ ...s, zile_scadenta: Number(e.target.value) }))}
            />
            <Button
              variant="contained"
              onClick={async () => {
                await onCreateRecurrence(form);
                setForm(defaultForm);
              }}
            >
              Adauga recurent
            </Button>
          </Paper>

          <Paper className="sf-table-shell">
            <Box className="sf-inline-actions">
              <Button variant="outlined" onClick={onRunJob}>
                Run monthly job now
              </Button>
              <Button variant="contained" color="secondary" onClick={onPredictCurrentMonth}>
                Predictie luna curenta (temporar)
              </Button>
            </Box>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Denumire</TableCell>
                  <TableCell>Serviciu</TableCell>
                  <TableCell>Valuta</TableCell>
                  <TableCell>Suma</TableCell>
                  <TableCell>Zi emitere</TableCell>
                  <TableCell>Ultima luna</TableCell>
                  <TableCell align="right">Actiune</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(recurrences || []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {onOpenVendor ? (
                        <Link
                          component="button"
                          underline="hover"
                          className="sf-vendor-link"
                          onClick={() => onOpenVendor?.(r)}
                        >
                          {r.denumire}
                        </Link>
                      ) : (
                        r.denumire
                      )}
                    </TableCell>
                    <TableCell>{r.serviciu}</TableCell>
                    <TableCell>{r.valuta}</TableCell>
                    <TableCell>{Number(r.suma_valuta || 0).toFixed(2)}</TableCell>
                    <TableCell>{r.zi_emitere}</TableCell>
                    <TableCell>{r.last_generated_month || "-"}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => onGenerateNow(r.id)}>
                        Generate now
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {tab === 1 && (
        <Paper className="sf-table-shell">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Denumire</TableCell>
                <TableCell>Luna</TableCell>
                <TableCell sortDirection={issueDateOrder} sx={{ whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active
                    direction={issueDateOrder}
                    onClick={() => setIssueDateOrder((order) => order === "desc" ? "asc" : "desc")}
                  >
                    Data emiterii
                  </TableSortLabel>
                </TableCell>
                <TableCell>Scadenta</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rest</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {generatedInvoices.map((i) => {
                const dueDateRaw = String(i?.termen_scadent || "").slice(0, 10);
                const dueDateObj = /^\d{4}-\d{2}-\d{2}$/.test(dueDateRaw)
                  ? new Date(`${dueDateRaw}T00:00:00`)
                  : null;
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const overdueDays =
                  dueDateObj && !Number.isNaN(dueDateObj.getTime()) && dueDateObj < today
                    ? Math.round((today.getTime() - dueDateObj.getTime()) / 86400000)
                    : 0;
                const isPriorityOverdue = overdueDays >= 30 && ["OPEN", "PARTIAL", "DRAFT"].includes(String(i?.status || "").toUpperCase());
                return (
                <TableRow key={i.id} className={isPriorityOverdue ? "sf-overdue-priority" : ""}>
                  <TableCell>
                    {onOpenVendor ? (
                      <Link
                        component="button"
                        underline="hover"
                        className="sf-vendor-link"
                        onClick={() => onOpenVendor?.(i)}
                      >
                        {i.vendor_name || i.denumire}
                      </Link>
                    ) : (
                      i.denumire
                    )}
                  </TableCell>
                  <TableCell>{i.luna}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {getInvoiceIssueDate(i.data_emitere) || "-"}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.35}>
                      <Typography variant="body2">{i.termen_scadent?.slice(0, 10) || "-"}</Typography>
                      {isPriorityOverdue ? (
                        <Typography variant="caption" sx={{ color: "#b91c1c", fontWeight: 800 }}>
                          Restanta veche: {overdueDays} zile
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>{i.status}</TableCell>
                  <TableCell>{Number(i.rest_plata_ron || 0).toFixed(2)}</TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}

export default RecurentTab;
